/**
 * Convertit une position X (client) en pourcentage le long de la timeline stack.
 * scrollContainerEl : élément qui scroll horizontalement (overlay mobile).
 * stackEl : .player-timeline-stack (référentiel des % segments / playhead).
 */
export function resolvePercentageFromTimelineX(clientX, scrollContainerEl, stackEl) {
  if (!stackEl || typeof clientX !== 'number') return null
  const rect = stackEl.getBoundingClientRect()
  const scrollLeft = scrollContainerEl?.scrollLeft || 0
  const scrollWidth = stackEl.scrollWidth || rect.width
  if (scrollWidth <= 0) return null
  const x = clientX - rect.left + scrollLeft
  return Math.max(0, Math.min(100, (x / scrollWidth) * 100))
}

/**
 * Convertit clientX en timestamp (secondes) dans l'extrait.
 */
export function resolveTimeFromTimelineX(clientX, scrollContainerEl, stackEl, options) {
  const {
    isMobile = false,
    chordSegments = [],
    desktopSegments = [],
    startTime = 0,
    endTime = 60,
  } = options || {}

  const percentage = resolvePercentageFromTimelineX(clientX, scrollContainerEl, stackEl)
  if (percentage == null) return null

  if (isMobile && chordSegments.length > 0) {
    const X = chordSegments.length
    const i = Math.min(X - 1, Math.max(0, Math.floor((percentage / 100) * X)))
    const q = ((percentage / 100) * X) % 1
    const seg = chordSegments[i]
    const segDuration = seg.endTime - seg.startTime
    return seg.startTime + q * segDuration
  }

  if (desktopSegments.length > 0) {
    const beforeStart = desktopSegments[0]
    const afterEnd = desktopSegments[desktopSegments.length - 1]
    if (percentage <= beforeStart.displayStartPos) {
      const beforeGapSpan = Math.max(beforeStart.displayStartPos, 0.0001)
      const beforeGapRatio = Math.max(0, Math.min(1, percentage / beforeGapSpan))
      return startTime + beforeGapRatio * (beforeStart.startTime - startTime)
    }
    if (percentage >= afterEnd.displayEndPos) {
      const trailingSpan = Math.max(100 - afterEnd.displayEndPos, 0.0001)
      const trailingRatio = Math.max(0, Math.min(1, (percentage - afterEnd.displayEndPos) / trailingSpan))
      return afterEnd.endTime + trailingRatio * (endTime - afterEnd.endTime)
    }
    const hitSegment = desktopSegments.find(
      (seg) => percentage >= seg.displayStartPos && percentage <= seg.displayEndPos
    )
    if (hitSegment) {
      const segDuration = hitSegment.endTime - hitSegment.startTime
      const span = Math.max(hitSegment.displayWidthPercent, 0.0001)
      const ratio = (percentage - hitSegment.displayStartPos) / span
      return hitSegment.startTime + Math.max(0, Math.min(1, ratio)) * segDuration
    }
    return startTime + (percentage / 100) * (endTime - startTime)
  }

  return startTime + (percentage / 100) * (endTime - startTime)
}

/**
 * Position du curseur de lecture en % (0–100) le long de la timeline.
 */
export function computePlayheadPositionPercent(time, options) {
  const {
    startTime = 0,
    endTime = 60,
    isMobile = false,
    chordSegments = [],
    desktopSegments = [],
  } = options || {}

  if (time < startTime || time > endTime) return null

  const relativeEndTime = endTime - startTime
  const relativeCurrentTime = time - startTime

  if (!isMobile && desktopSegments.length > 0) {
    const firstSegment = desktopSegments[0]
    const lastSegment = desktopSegments[desktopSegments.length - 1]
    if (time <= firstSegment.startTime) {
      const preGapDuration = Math.max(0, firstSegment.startTime - startTime)
      const preGapProgress = preGapDuration > 0
        ? Math.max(0, Math.min(1, (time - startTime) / preGapDuration))
        : 1
      return preGapProgress * firstSegment.displayStartPos
    }
    for (let i = 0; i < desktopSegments.length; i++) {
      const seg = desktopSegments[i]
      const segDuration = seg.endTime - seg.startTime
      if (segDuration <= 0) continue
      if (time >= seg.startTime && time <= seg.endTime) {
        const p = (time - seg.startTime) / segDuration
        return seg.displayStartPos + p * seg.displayWidthPercent
      }
    }
    for (let i = 0; i < desktopSegments.length - 1; i++) {
      if (time > desktopSegments[i].endTime && time < desktopSegments[i + 1].startTime) {
        const gapDur = desktopSegments[i + 1].startTime - desktopSegments[i].endTime
        const p = gapDur > 0 ? (time - desktopSegments[i].endTime) / gapDur : 0
        const gapStartPos = desktopSegments[i].displayEndPos
        const gapEndPos = desktopSegments[i + 1].displayStartPos
        return gapStartPos + p * (gapEndPos - gapStartPos)
      }
    }
    if (time >= lastSegment.endTime) {
      const trailingDuration = Math.max(0, endTime - lastSegment.endTime)
      const trailingProgress = trailingDuration > 0
        ? Math.max(0, Math.min(1, (time - lastSegment.endTime) / trailingDuration))
        : 1
      return lastSegment.displayEndPos + trailingProgress * (100 - lastSegment.displayEndPos)
    }
    return relativeEndTime > 0 ? (relativeCurrentTime / relativeEndTime) * 100 : 0
  }

  if (!chordSegments.length) {
    return relativeEndTime > 0 ? (relativeCurrentTime / relativeEndTime) * 100 : 0
  }

  const X = chordSegments.length
  for (let i = 0; i < chordSegments.length; i++) {
    const seg = chordSegments[i]
    const segDuration = seg.endTime - seg.startTime
    if (segDuration <= 0) continue
    if (time >= seg.startTime && time <= seg.endTime) {
      const p = (time - seg.startTime) / segDuration
      return ((i + p) / X) * 100
    }
  }
  for (let i = 0; i < chordSegments.length - 1; i++) {
    if (time > chordSegments[i].endTime && time < chordSegments[i + 1].startTime) {
      const gapDur = chordSegments[i + 1].startTime - chordSegments[i].endTime
      const p = gapDur > 0 ? (time - chordSegments[i].endTime) / gapDur : 0
      const gapStartPos = chordSegments[i].endPos
      const gapEndPos = chordSegments[i + 1].startPos
      return gapStartPos + p * (gapEndPos - gapStartPos)
    }
  }
  if (time < chordSegments[0].startTime) return 0
  if (time > chordSegments[chordSegments.length - 1].endTime) return 100
  return relativeEndTime > 0 ? (relativeCurrentTime / relativeEndTime) * 100 : 0
}
